import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {secondsToMinutes} from "./helpers/secondsToMinutes";
import { getApiErrorMessage } from "./helpers/apiErrorMessage";

function Albums() {
    const [query, setQuery] = useState('');
    const [albumList, setAlbumList] = useState([]);
    const [expandedDetails, setExpandedDetails] = useState(null);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => { fetchAlbumList(); }, []);

    const fetchAlbumList = async () => {
        try {
            setError(null);
            const response = await axios.get(`/api/admin/albums`);
            setAlbumList(response.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load albums'));
            console.error('Error fetching albums:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    const toggleEditing = (index) => {
        setExpandedDetails(null)
        setEditing(editing === index ? null : index)
    }

    const toggleDetails = (index) => {
        setEditing(null);
        setExpandedDetails(expandedDetails === index ? null : index);
    };

    const handleInputChange = async (index, mbid) => {
        const newTitle = document.getElementById(`newTitle${index}`).value;
        const newTags = document.getElementById(`newTags${index}`).value;

        const updatedAlbumList = [...albumList];
        updatedAlbumList[index]['title'] = newTitle;
        updatedAlbumList[index]['tags'] = newTags.split(',').map(tag => tag.trim());
        setAlbumList(updatedAlbumList);

        try {
            setError(null);
            await axios.post(`/api/admin/albums/change`, {
                mbid: mbid,
                newTitle: newTitle,
                newTags: JSON.stringify(newTags.split(',').map(tag => tag.trim()))
            });
        } catch (error) {
            setError(getApiErrorMessage(error, 'update this album'));
            console.error("Error changing album: ", error);
        }
    }

    const handleDelete = async (index, mbid) => {
        try {
            setError(null);
            const response = await axios.delete(`/api/admin/albums/${mbid}`);  // TODO
            console.log(response.data.message);

            const updatedAlbumList = [...albumList];
            updatedAlbumList.splice(index, 1);
            setAlbumList(updatedAlbumList);  // change at the frontend
            setEditing(null);
        } catch (error) {
            setError(getApiErrorMessage(error, 'delete this album'));
            console.error("Error deleting album: ", error);
        }
    }

    return (
        <div className="bg-gray-900 text-white font-sans min-h-screen">
        <div>
            {error && (
                <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200 text-2xl">×</button>
                    </div>
                </div>
            )}
            <div className="flex flex-col items-center w-full">
                <div className="flex items-center w-full max-w-lg">
                    <input
                        type="text"
                        placeholder="Search Album"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="flex-grow bg-gray-700 text-gray-300 p-2 mb-5 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                </div>
            </div>

            <div className="mt-6 ml-40 mr-40">
                {albumList
                    .filter(entry => entry.title.toLowerCase().includes(query.toLowerCase()) || entry.artist.toLowerCase().includes(query.toLowerCase()))
                    .map((entry, index) => {
                    return (
                        <div key={index} className="flex flex-col hover:bg-gray-800 transition-colors duration-200">
                            <div className="flex items-center border-t border-gray-700 py-4 px-4">
                                <div className="ml-3 flex-grow flex items-center">
                                    <img
                                        src={entry.albumCover || 'https://placehold.co/75x75'}
                                        alt={entry.title}
                                        className="inline-block mr-4 w-12 h-12 rounded-lg"
                                    />
                                    <div className="flex flex-col">
                                        <div className="flex items-center">
                                            <span className="font-bold text-lg">{entry.title}</span>
                                            <p className="text-gray-400 text-sm ml-1.5 release-date">{entry.year}</p>
                                        </div>
                                        <p className="text-gray-400 text-sm">{entry.artist}</p>
                                    </div>
                                </div>
                                <div className="flex flex-center items-center justify-center mr-6">
                                    <i
                                        className={`fas fa-pencil-alt text-gray-${editing === index ? '300' : '500'} mr-2 cursor-pointer hover:text-gray-300`}
                                        onClick={() => toggleEditing(index)}
                                    ></i>
                                    <i
                                        className={`fas fa-book text-gray-${expandedDetails === index ? '300' : '500'} mr-2 cursor-pointer hover:text-gray-300`}
                                        onClick={() => toggleDetails(index)}
                                    ></i>
                                </div>
                            </div>
                            <div
                                className={`overflow-hidden transition-all duration-1000 ease-in-out ${
                                    expandedDetails === index ? 'h-96' : 'h-0'
                                }`}
                            >
                                {expandedDetails === index && (
                                    <div className="flex items-start space-x-20">
                                        <div className="ml-28 mb-4 text-gray-300 space-y-1">
                                            <div className="grid grid-cols-2 w-96 space-y-1">
                                                <p><strong>Artist:</strong> {entry.artist}</p>
                                                <p><strong>Title:</strong> {entry.title}</p>
                                                <p><strong>Country:</strong> {entry.country}</p>
                                                <p><strong>Year:</strong> {entry.year}</p>
                                            </div>
                                            <p><strong>MBID:</strong> {entry.mbid}</p>
                                            <p><strong>Duration:</strong> {secondsToMinutes(entry.duration)}</p>
                                            <p><strong>Added on:</strong> {entry.addedOn}</p>
                                        </div>
                                        <img
                                            src={entry.albumCover || 'https://placehold.co/75x75'}
                                            alt={entry.title}
                                            className="inline-block w-[370px] justify-right"
                                        />
                                        <p><strong>Tags:</strong></p>
                                        <p className="ml-10 w-48">{entry.tags.join(', ')}</p>
                                    </div>
                                )}
                            </div>
                            <div
                                className={`overflow-hidden transition-all duration-1000 ease-in-out ${
                                    editing === index ? 'h-48' : 'h-0'
                                }`}
                            >
                                {editing === index && (
                                    <div className="flex items-start space-x-20">
                                        <div
                                            className="flex flex-col ml-28 gap-4 p-4 rounded-lg shadow-lg w-full max-w-lg">
                                            <div className="flex items-start gap-2">
                                                <label htmlFor="title" className="text-white w-24">Album title:</label>
                                                <input
                                                    type="text"
                                                    id={`newTitle${index}`}
                                                    defaultValue={entry.title}
                                                    className="flex-1 px-2 py-1 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                                />
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <label htmlFor="tags" className="text-white w-24">Tags:</label>
                                                <input
                                                    type="text"
                                                    id={`newTags${index}`}
                                                    defaultValue={entry.tags}
                                                    className="flex-1 px-2 py-1 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => handleInputChange(index, entry.mbid)}
                                            className="ml-2 p-2 text-gray-500 bg-transparent rounded hover:bg-green-100 hover:text-green-600"
                                        >
                                            <i className="fas fa-check"/>
                                        </button>
                                        <button
                                            onClick={(e) => handleDelete(index, entry.mbid)}
                                            className="ml-2 p-2 text-gray-500 bg-transparent rounded hover:bg-red-100 hover:text-red-600"
                                        >
                                            <i className="fas fa-trash-alt"/>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                    })}
            </div>
        </div>
        </div>
    )
}

export default Albums;